import { Page, Modal, TextField, Select } from '../components/forms';
/**
 * INTERNATIONAL DOCUMENTS PAGE
 *
 * Generate and manage customs forms, certificates of origin, etc.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface InternationalDocument {
  id: number;
  order_id: number;
  order_number: string;
  doc_type: string;
  document_number: string | null;
  destination_country: string;
  total_value: number;
  currency: string;
  items: DocumentItem[];
  file_r2_key: string | null;
  status: string;
  generated_at: string | null;
  generated_by: string | null;
  created_at: string;
}

interface DocumentItem {
  description: string;
  hs_code: string;
  quantity: number;
  value: number;
  weight: number;
  country_of_origin: string;
}

export default function InternationalDocuments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi, loading, error } = useApi();
  const { fetchApi: generateApi, error: generateError } = useApi();
  const [documents, setDocuments] = useState<InternationalDocument[]>([]);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [docType, setDocType] = useState('customs_invoice');
  const [generating, setGenerating] = useState(false);

  // If orderId is in URL params, open modal for that order
  const orderIdParam = searchParams.get('orderId');

  const loadDocuments = useCallback(async () => {
    const data = await fetchApi<{ data: InternationalDocument[] }>(
      '/international/documents?limit=100'
    );
    if (data?.data) {
      setDocuments(data.data);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (orderIdParam) {
      setSelectedOrderId(orderIdParam);
      setGenerateModalOpen(true);
    }
  }, [orderIdParam]);

  const handleGenerate = async () => {
    if (!selectedOrderId.trim() || generating) return;

    setGenerating(true);
    const endpoint =
      docType === 'customs_invoice'
        ? `/international/orders/${encodeURIComponent(selectedOrderId.trim())}/customs-invoice`
        : `/international/orders/${encodeURIComponent(selectedOrderId.trim())}/certificate-of-origin`;

    const result = await generateApi(endpoint, { method: 'POST', body: '{}' });
    setGenerating(false);

    if (result) {
      setGenerateModalOpen(false);
      setSelectedOrderId('');
      loadDocuments();
    }
  };

  const handleDownload = async (doc: InternationalDocument) => {
    if (!doc.file_r2_key) return;

    const result = await fetchApi<{ url: string }>(
      `/international/documents/${doc.id}/download`
    );
    if (result?.url) {
      window.open(result.url, '_blank');
    }
  };

  const getDocTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      customs_invoice: 'Customs Invoice',
      certificate_of_origin: 'Certificate of Origin',
      cn22: 'CN22 Form',
      cn23: 'CN23 Form',
    };
    return labels[type] || type;
  };

  const getDocTypeBadge = (type: string) => {
    const tones: Record<string, 'info' | 'success' | 'warning' | undefined> = {
      customs_invoice: 'info',
      certificate_of_origin: 'success',
      cn22: undefined,
      cn23: undefined,
    };
    return <Badge tone={tones[type]}>{getDocTypeLabel(type)}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'success' | 'warning' | 'critical' | undefined> = {
      draft: 'warning',
      generated: 'success',
      sent: 'info' as 'success',
    };
    return <Badge tone={tones[status]}>{status}</Badge>;
  };

  const formatCurrency = (amount: number, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  if (loading && documents.length === 0) {
    return (
      <Page title="International Documents">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading documents...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="International Documents"
      subtitle="Customs forms and certificates for international shipments"
      primaryAction={{
        content: 'Generate Document',
        onAction: () => setGenerateModalOpen(true),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Info Banner */}
        <Layout.Section>
          <Banner tone="info">
            Generate customs invoices and certificates of origin for international
            orders. Documents can also be generated from individual order pages.
          </Banner>
        </Layout.Section>

        {/* Documents Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {documents.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
                  headings={['Order', 'Type', 'Country', 'Doc Number', 'Value', 'Status', 'Actions']}
                  rows={documents.map((doc) => [
                    <Button
                      key={`order-${doc.id}`}
                      variant="plain"
                      onClick={() => navigate(`/orders/${doc.order_id}`)}
                    >
                      {doc.order_number}
                    </Button>,
                    getDocTypeBadge(doc.doc_type),
                    doc.destination_country,
                    doc.document_number || '-',
                    formatCurrency(doc.total_value, doc.currency),
                    getStatusBadge(doc.status),
                    <InlineStack key={`actions-${doc.id}`} gap="200">
                      {doc.file_r2_key && (
                        <Button size="slim" onClick={() => handleDownload(doc)}>
                          Download
                        </Button>
                      )}
                    </InlineStack>,
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No international documents"
                  action={{
                    content: 'Generate Document',
                    onAction: () => setGenerateModalOpen(true),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Generate customs invoices and certificates of origin for your
                    international shipments.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Document Types Info */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Document Types</Text>

              <BlockStack gap="300">
                <InlineStack gap="400" blockAlign="start">
                  <div style={{ width: '180px' }}>
                    <Badge tone="info">Customs Invoice</Badge>
                  </div>
                  <Text as="p" tone="subdued">
                    Required for commercial shipments outside the EU. Lists all items
                    with HS codes, values, and country of origin.
                  </Text>
                </InlineStack>

                <InlineStack gap="400" blockAlign="start">
                  <div style={{ width: '180px' }}>
                    <Badge tone="success">Certificate of Origin</Badge>
                  </div>
                  <Text as="p" tone="subdued">
                    Certifies where goods were manufactured. Required for preferential
                    tariff rates under trade agreements.
                  </Text>
                </InlineStack>

                <InlineStack gap="400" blockAlign="start">
                  <div style={{ width: '180px' }}>
                    <Badge>CN22 Form</Badge>
                  </div>
                  <Text as="p" tone="subdued">
                    Small customs declaration for postal items under €300. Attached
                    directly to the package.
                  </Text>
                </InlineStack>

                <InlineStack gap="400" blockAlign="start">
                  <div style={{ width: '180px' }}>
                    <Badge>CN23 Form</Badge>
                  </div>
                  <Text as="p" tone="subdued">
                    Detailed customs declaration for postal items over €300 or
                    requiring detailed item listing.
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Generate Document Modal */}
      <Modal
        open={generateModalOpen}
        onClose={() => {
          if (generating) return;
          setGenerateModalOpen(false);
          setSelectedOrderId('');
        }}
        title="Generate International Document"
        primaryAction={{
          content: 'Generate',
          onAction: handleGenerate,
          loading: generating,
          disabled: !/^[1-9]\d*$/.test(selectedOrderId.trim()) || generating,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            disabled: generating,
            onAction: () => {
              setGenerateModalOpen(false);
              setSelectedOrderId('');
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {generateError && <Banner tone="critical">{generateError}</Banner>}
            <TextField
              label="Order ID"
              requiredIndicator value={selectedOrderId}
              onChange={setSelectedOrderId}
              autoComplete="off"
              disabled={generating}
              placeholder="Enter the internal order ID"
              helpText="Use the number in the Whenly order page address, not the Shopify order number."
            />

            <Select
              label="Document Type"
              options={[
                { label: 'Customs Invoice', value: 'customs_invoice' },
                { label: 'Certificate of Origin', value: 'certificate_of_origin' },
                { label: 'CN22 Form (not available)', value: 'cn22', disabled: true },
                { label: 'CN23 Form (not available)', value: 'cn23', disabled: true },
              ]}
              requiredIndicator value={docType}
              onChange={setDocType}
              disabled={generating}
            />

            <Banner tone="info">
              The document will be generated using the order's shipping address,
              line items, and product data (HS codes, weights, country of origin).
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
